import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import {
  createLivePriceCardElement,
  updateLivePriceCardElement,
} from "../services/priceCardElementService.js";

const TYPES = [
  "product",
  "service",
  "access_condition",
  "transport_tariff",
  "distance_rate",
  "surcharge",
  "multiplier",
  "included",
];

const initialForm = (name, element) => ({
  name: element?.name || name || "",
  description: element?.description || "",
  type: element?.type || "service",
  active: element?.configuredActive ?? (element?.active !== false),
  group: element?.group || "",
  control: element?.control || "quantity",
  side: element?.side || "",
  unit: element?.unit || "item",
  amount: element?.unitPrice ?? element?.pricing?.amount ?? "",
  pricingMode: element?.pricing?.mode || "",
  min: element?.min ?? "",
  max: element?.max ?? "",
  step: element?.step ?? "",
  sortOrder: element?.sortOrder ?? "",
  effectiveFrom: element?.effectiveFrom?.slice?.(0, 10) || "",
  effectiveTo: element?.effectiveTo?.slice?.(0, 10) || "",
  optionsJson: JSON.stringify(element?.options || [], null, 2),
  mutuallyExclusiveJson: JSON.stringify(
    element?.mutuallyExclusiveWith || [],
    null,
    2,
  ),
  reason: "",
});

export default function CreatePriceCardElementDialog({
  open,
  initialName,
  initialElement,
  onClose,
  onCreated,
}) {
  const [form, setForm] = useState(initialForm(initialName));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialForm(initialName, initialElement));
      setError("");
    }
  }, [initialElement, initialName, open]);

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const options = JSON.parse(form.optionsJson || "[]");
      const mutuallyExclusiveWith = JSON.parse(
        form.mutuallyExclusiveJson || "[]",
      );
      if (!Array.isArray(options) || !Array.isArray(mutuallyExclusiveWith)) {
        throw new Error("Options and mutual exclusions must be JSON arrays");
      }
      const payload = {
        ...form,
        active: form.active,
        amount: form.type === "included" ? 0 : Number(form.amount),
        pricingMode:
          form.pricingMode ||
          (form.type === "included"
            ? "included"
            : form.type === "multiplier"
              ? "multiplier"
              : "per_unit"),
        min: form.min === "" ? null : Number(form.min),
        max: form.max === "" ? null : Number(form.max),
        step: form.step === "" ? null : Number(form.step),
        sortOrder: form.sortOrder === "" ? undefined : Number(form.sortOrder),
        effectiveFrom: form.effectiveFrom || null,
        effectiveTo: form.effectiveTo || null,
        options,
        mutuallyExclusiveWith,
      };
      delete payload.optionsJson;
      delete payload.mutuallyExclusiveJson;
      const element = initialElement?.id
        ? await updateLivePriceCardElement(initialElement.id, payload)
        : await createLivePriceCardElement(payload);
      onCreated(element);
      onClose();
    } catch (submitError) {
      setError(submitError.message || "Tariff could not be created");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {initialElement ? "Edit live price-card element" : "Create missing price-card element"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Name" value={form.name} onChange={update("name")} required />
          <TextField
            label="Description"
            value={form.description}
            onChange={update("description")}
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    active: event.target.checked,
                  }))
                }
              />
            }
            label="Active and exposed to the PWA"
          />
          <TextField select label="Type" value={form.type} onChange={update("type")}>
            {TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="PWA control"
            value={form.control}
            onChange={update("control")}
          >
            {["toggle", "quantity", "number", "select", "informational"].map((control) => (
              <MenuItem key={control} value={control}>
                {control}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Unit" value={form.unit} onChange={update("unit")} />
          <Stack direction="row" spacing={1}>
            <TextField label="Group" value={form.group} onChange={update("group")} />
            <TextField label="Side" value={form.side} onChange={update("side")} />
            <TextField
              label="Order"
              type="number"
              value={form.sortOrder}
              onChange={update("sortOrder")}
            />
          </Stack>
          {form.type !== "included" && (
            <TextField
              label={form.type === "multiplier" ? "Multiplier" : "Price"}
              type="number"
              value={form.amount}
              onChange={update("amount")}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />
          )}
          <Stack direction="row" spacing={1}>
            <TextField label="Minimum" type="number" value={form.min} onChange={update("min")} />
            <TextField label="Maximum" type="number" value={form.max} onChange={update("max")} />
            <TextField label="Step" type="number" value={form.step} onChange={update("step")} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              label="Effective from"
              type="date"
              value={form.effectiveFrom}
              onChange={update("effectiveFrom")}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Effective to"
              type="date"
              value={form.effectiveTo}
              onChange={update("effectiveTo")}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <TextField
            label="Select options (JSON array)"
            value={form.optionsJson}
            onChange={update("optionsJson")}
            multiline
            minRows={2}
          />
          <TextField
            label="Mutually exclusive element IDs (JSON array)"
            value={form.mutuallyExclusiveJson}
            onChange={update("mutuallyExclusiveJson")}
            multiline
            minRows={2}
          />
          <TextField
            label={initialElement ? "Reason for this change" : "Reason for creating this tariff"}
            value={form.reason}
            onChange={update("reason")}
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={saving || !form.name.trim() || !form.reason.trim()}
        >
          {initialElement ? "Save as new live revision" : "Create and publish"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
